import { useEffect, useRef, useState } from "react";
import { useLazyGetEmployeePhotoBlobQuery } from "./employeesApi";

/**
 * Page-level cache of employee enrollment photos, keyed by employeeRecordGuid.
 *
 * A bare `<img src="{apiUrl}/photo">` cannot attach this app's bearer token, so
 * every photo has to come through the API slice as a blob and be handed to the
 * <img> as an object URL. This fetches each guid exactly once no matter how many
 * rows that employee appears on, and revokes every URL it made when the caller
 * unmounts - the same shared-cache behaviour as the Flutter screens'
 * employeePhotoProvider family, rather than each card fetching and decoding the
 * same blob for itself.
 *
 * A guid maps to `undefined` (not fetched yet / still in flight), `null` (fetched
 * and there is no photo - a 404), or the object URL. Callers render initials for
 * anything that is not a string, so the three states need no separate flag.
 *
 * Extracted from GuardLookupPage when the Attendance Dashboard was ported
 * (2026-09-09) and needed the identical behaviour - both use this one copy.
 */
export const useEmployeePhotoCache = (rows, getGuid = (row) => row?.employeeRecordGuid) => {
  const [photoUrls, setPhotoUrls] = useState({});
  const [fetchPhoto] = useLazyGetEmployeePhotoBlobQuery();

  // Mirrors state so the fetch effect can test "already requested?" without
  // listing photoUrls as a dependency, which would re-run it on every arrival.
  const photoUrlsRef = useRef(photoUrls);
  photoUrlsRef.current = photoUrls;

  useEffect(() => {
    (rows || []).forEach((row) => {
      const guid = getGuid(row);
      if (!guid || guid in photoUrlsRef.current) return;
      photoUrlsRef.current = { ...photoUrlsRef.current, [guid]: undefined };
      setPhotoUrls((prev) => ({ ...prev, [guid]: undefined }));
      fetchPhoto(guid)
        .unwrap()
        .then((blob) => setPhotoUrls((prev) => ({ ...prev, [guid]: URL.createObjectURL(blob) })))
        .catch(() => setPhotoUrls((prev) => ({ ...prev, [guid]: null })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, fetchPhoto]);

  // Revoked on unmount only. The empty dep array is deliberate: the cleanup reads
  // the ref's LATEST value at unmount time, not whatever the map held when this
  // effect first ran.
  useEffect(
    () => () => {
      Object.values(photoUrlsRef.current).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    },
    []
  );

  return photoUrls;
};

export default useEmployeePhotoCache;
